/**
 * What each step insists on before it will let somebody past it.
 *
 * Per step rather than for the form as a whole, and that is the point of a
 * wizard: a page of forty fields reddening at once is unanswerable, and the
 * person on the other end of this link is on a phone deciding whether it is
 * worth finishing. Continue checks the step it is on and nothing else.
 *
 * The rules are deliberately light. This form is a courtesy a client is doing
 * their practitioner, not an application being assessed — most of it is
 * optional, and what is required is only what a plan cannot be written without
 * or what nobody could be reached at. Anything the practitioner can fill in
 * later against the record is left open here.
 */

import type { PreconsultationStepId } from "./preconsultationSteps";
import type { PreconsultationAnswers } from "./types/preconsultation.types";

export type PreconsultationField = keyof PreconsultationAnswers;

export type PreconsultationErrors = Partial<
  Record<PreconsultationField, string>
>;

/** What every box holds before anything is typed into it. */
export const EMPTY_ANSWERS: PreconsultationAnswers = {
  firstName: "",
  lastName: "",
  dob: "",
  age: "",
  gender: "",
  goals: [],
  lifestyle: "",
  email: "",
  phone: "",

  address: "",
  state: "",
  locationCity: "",
  pincode: "",

  height: "",
  heightUnit: "cm",
  weight: "",
  weightUnit: "kg",
  targetWeight: "",
  targetWeightUnit: "kg",

  foodPreference: "",
  cuisines: [],
  dishesLoved: [],
  dishesAvoided: [],
  foodAllergies: [],

  profession: "",
  workFrom: "",
  workTo: "",
  workAnyHours: false,
  sleepFrom: "",
  sleepTo: "",
  sleepAnyHours: false,
  travel: "",
  alcohol: "",
  smoking: "",

  conditions: [],
  onMedication: "",
  medications: [],
};

/** Anything but a digit, dropped as it is typed. */
export const digitsOnly = (value: string): string => value.replace(/\D/g, "");

/**
 * A name as it is typed: letters, spaces and the punctuation names carry.
 * Digits are dropped rather than complained about — nobody's name has one, so
 * the keystroke is a slip rather than an answer worth reddening a field over.
 */
export const normaliseNameInput = (value: string): string =>
  value.replace(/[^\p{L}\s'.-]/gu, "").replace(/\s{2,}/g, " ");

/** The oldest age this form will read off a date of birth rather than as a
 *  slip. There is no age box any more — see `BasicsStep`. */
const MAX_AGE = 120;

/**
 * A date of birth as it is typed: digits only, split by the slashes the field
 * shows. The separators are painted rather than typed, so a date pasted in as
 * `26-03-1998` still lands in the right three boxes.
 */
export const normaliseDobInput = (raw: string): string => {
  const digits = digitsOnly(raw).slice(0, 8);

  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
    .filter(Boolean)
    .join("/");
};

const DOB_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * The day a `DD/MM/YYYY` names, or nothing where it names none.
 *
 * Read against the calendar rather than against the pattern: `31/02/1998`
 * matches the shape perfectly and is still not a day, and `new Date(1998, 1,
 * 31)` rolls forward to 3 March rather than failing — so the parts are read
 * back off the date to catch it.
 *
 * The same rule the Add client form applies to the same field, spelled again
 * here rather than imported: this form renders with nobody signed in, and
 * nothing on it may reach into the dashboard's own features.
 */
export const dobDate = (value: string): Date | undefined => {
  const match = DOB_PATTERN.exec(value.trim());
  if (!match) return undefined;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : undefined;
};

/** The oldest day the field will take, which is what its calendar offers. */
export const EARLIEST_DOB = new Date(new Date().getFullYear() - MAX_AGE, 0, 1);

/** A date of birth: a real day, behind us, and within a lifetime. */
const dobError = (value: string): string | undefined => {
  const date = dobDate(value);
  if (!date) return "Enter the date as DD/MM/YYYY";
  if (date > new Date()) return "Date of birth cannot be in the future";
  if (date < EARLIEST_DOB) return "Enter a valid date of birth";
  return undefined;
};

/**
 * Their age on today's date, out of a date of birth — `"26"`, or empty where
 * the box holds no day yet.
 *
 * Counted rather than asked twice: somebody who gives their birthday has
 * already answered the age question, and a form that then made them work out
 * the arithmetic itself would be asking them to check our sum.
 */
export const ageFromDob = (value: string): string => {
  const born = dobDate(value);
  if (!born) return "";

  const today = new Date();
  let years = today.getFullYear() - born.getFullYear();
  // A birthday still to come this year has not been had yet.
  const month = today.getMonth() - born.getMonth();
  if (month < 0 || (month === 0 && today.getDate() < born.getDate())) years -= 1;

  return years >= 0 && years <= MAX_AGE ? `${years}` : "";
};

const required = (value: string, what: string): string | undefined =>
  value.trim() ? undefined : `${what} is required`;

/**
 * The step's own rules.
 *
 * Only ever asked about the step being left, so a field two steps ahead is not
 * an error yet — it is simply a question nobody has reached.
 */
export const validateStep = (
  step: PreconsultationStepId,
  answers: PreconsultationAnswers
): PreconsultationErrors => {
  const errors: PreconsultationErrors = {};

  if (step === "basics") {
    /*
      One rule, because there is one answer on this step the reader can get
      wrong.

      The step is not a set of questions any more — it is what the practitioner
      already put on file, shown back to be checked, with the two fields that
      are genuinely the client's left open. See `PreconsultationBasics`.
      Everything else is drawn locked, and a rule about a locked field is a
      Continue that can never be satisfied: the person holding the link would
      be told their last name is required by a box they are not allowed to type
      into. Whatever is missing there is missing from the record, and the
      record is the practitioner's to fix.

      A life style is a closed list and cannot be malformed, which leaves the
      date of birth — and only one that was actually typed, since the whole
      point of the field is that a great many records do not have one.
    */
    if (answers.dob.trim()) errors.dob = dobError(answers.dob);
  }

  if (step === "location") {
    // A pincode is either six digits or not given at all: this is the one
    // field on the step anybody could mistype into something meaningless, and
    // an address is what a home visit or a courier is worked out from.
    if (answers.pincode && answers.pincode.length !== 6) {
      errors.pincode = "Enter the 6-digit pincode";
    }
  }

  if (step === "body") {
    // Height and weight are what everything on a plan is calculated off, so
    // they are the two the form will not go past. A target is a wish and may
    // honestly be unknown.
    errors.height = required(answers.height, "Height");
    errors.weight = required(answers.weight, "Weight");
  }

  // Food, day and health ask about habits. Everything on those three is
  // optional by design: a blank answer is itself an answer the practitioner
  // can act on, and there is no plan that cannot be written without knowing
  // somebody's favourite cuisine.

  (Object.keys(errors) as PreconsultationField[]).forEach((field) => {
    if (!errors[field]) delete errors[field];
  });

  return errors;
};

